import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Play, Clock, Repeat } from "lucide-react";
import {
  parseOffset,
  formatOffset,
  effectiveHoldSeconds,
  type PlaylistItem,
  type PlaylistTrigger,
  type PlaylistWithItems,
  type PlayoutWithAsset,
  type TriggerStatus,
} from "@production-hub/shared";
import {
  usePlaylists,
  usePlayouts,
  useCreatePlaylist,
  useUpdatePlaylist,
  useDeletePlaylist,
  useCreatePlaylistItem,
  useUpdatePlaylistItem,
  useDeletePlaylistItem,
  useCreateTrigger,
  useUpdateTrigger,
  useDeleteTrigger,
  useStartPlaylist,
  usePlayoutReadiness,
} from "@/hooks/useBridgePlayout";

interface PlaylistPanelProps {
  bridgeUrl: string | undefined | null;
}

/** Triggers are stored UTC; show them in the browser's zone. */
function formatLocal(iso: string): string {
  return new Date(iso).toLocaleString();
}

const TRIGGER_BADGE: Record<
  TriggerStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  pending: { label: "Scheduled", variant: "outline" },
  started: { label: "Started", variant: "secondary" },
  skipped: { label: "Skipped — already running", variant: "outline" },
  missed: { label: "Missed", variant: "destructive" },
  disabled: { label: "Off", variant: "outline" },
};

/** `datetime-local` wants wall-clock, not UTC. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
}

export function PlaylistPanel({ bridgeUrl }: PlaylistPanelProps) {
  const { data: playlists = [] } = usePlaylists(bridgeUrl);
  const { data: playouts = [] } = usePlayouts(bridgeUrl);
  const createPlaylist = useCreatePlaylist(bridgeUrl);
  const [newName, setNewName] = useState("");

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Playlists</CardTitle>
          <CardDescription>
            Items fire automatically at their offset from the run&apos;s start.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            placeholder="Playlist name"
          />
          <Button
            onClick={() => {
              createPlaylist.mutate({ name: newName });
              setNewName("");
            }}
            disabled={!newName}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add
          </Button>
        </CardContent>
      </Card>

      {playlists.map((playlist) => (
        <PlaylistCard
          key={playlist.id}
          playlist={playlist}
          playouts={playouts}
          bridgeUrl={bridgeUrl}
        />
      ))}
    </div>
  );
}

function PlaylistCard({
  playlist,
  playouts,
  bridgeUrl,
}: {
  playlist: PlaylistWithItems;
  playouts: PlayoutWithAsset[];
  bridgeUrl: string | undefined | null;
}) {
  const { data: readiness } = usePlayoutReadiness(bridgeUrl, playlist.id);
  const createItem = useCreatePlaylistItem(bridgeUrl);
  const deleteItem = useDeletePlaylistItem(bridgeUrl);
  const createTrigger = useCreateTrigger(bridgeUrl);
  const deleteTrigger = useDeleteTrigger(bridgeUrl);
  const deletePlaylist = useDeletePlaylist(bridgeUrl);
  const startPlaylist = useStartPlaylist(bridgeUrl);

  const [offset, setOffset] = useState("2:00");
  const [playoutId, setPlayoutId] = useState("");
  const [triggerAt, setTriggerAt] = useState("");

  const offsetSeconds = parseOffset(offset);

  return (
    <Card data-testid="playlist-card">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle>{playlist.name}</CardTitle>
          <CardDescription>
            {playlist.items.length} item(s), {playlist.triggers.length} start
            time(s)
            {playlist.loop_enabled &&
              ` · loops${playlist.loop_seconds ? ` every ${formatOffset(playlist.loop_seconds)}` : ""}`}
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={() =>
              startPlaylist.mutate({
                playlistId: playlist.id,
                force: !readiness?.ready,
              })
            }
            data-testid="start-now"
          >
            <Play className="mr-1 h-3 w-3" />
            Start now
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => deletePlaylist.mutate(playlist.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <LoopControls playlist={playlist} bridgeUrl={bridgeUrl} />

        {readiness && !readiness.ready && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm">
            {readiness.problems.map((problem) => (
              <div key={problem}>{problem}</div>
            ))}
            <div className="mt-1 text-xs text-muted-foreground">
              Start now will run anyway, as a rehearsal.
            </div>
          </div>
        )}

        <div className="space-y-1">
          {playlist.items.map((item) => (
            <ItemRow
              key={item.id}
              item={item}
              playouts={playouts}
              bridgeUrl={bridgeUrl}
              onDelete={() => deleteItem.mutate(item.id)}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <div className="min-w-[160px] flex-1 space-y-1">
            <Label className="text-xs">Playout</Label>
            <Select value={playoutId} onValueChange={setPlayoutId}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {playouts.map((playout) => (
                  <SelectItem key={playout.id} value={playout.id}>
                    {playout.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32 space-y-1">
            <Label className="text-xs">Offset</Label>
            <Input
              value={offset}
              onChange={(event) => setOffset(event.target.value)}
              placeholder="mm:ss"
              aria-invalid={offsetSeconds === null}
              className={offsetSeconds === null ? "border-destructive" : ""}
            />
          </div>
          <Button
            size="sm"
            onClick={() => {
              if (offsetSeconds === null || !playoutId) return;
              // Hold is left unset: the asset's own length applies unless the
              // operator overrides it on the row afterwards.
              createItem.mutate({
                playlistId: playlist.id,
                data: { playout_id: playoutId, offset_seconds: offsetSeconds },
              });
            }}
            disabled={!playoutId || offsetSeconds === null}
          >
            <Plus className="mr-1 h-3 w-3" />
            Add item
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            A bare number is minutes — <span className="font-mono">20</span> is
            20 minutes. Use <span className="font-mono">mm:ss</span> or{" "}
            <span className="font-mono">hh:mm:ss</span> for anything finer.
          </p>
        </div>

        <div className="space-y-1 border-t pt-3">
          <Label className="text-xs">Scheduled starts</Label>
          {playlist.triggers.map((trigger) => (
            <TriggerRow
              key={trigger.id}
              trigger={trigger}
              bridgeUrl={bridgeUrl}
              onDelete={() => deleteTrigger.mutate(trigger.id)}
            />
          ))}
          <div className="flex gap-2 pt-1">
            <Input
              type="datetime-local"
              value={triggerAt}
              onChange={(event) => setTriggerAt(event.target.value)}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!triggerAt}
              onClick={() => {
                createTrigger.mutate({
                  playlistId: playlist.id,
                  data: {
                    starts_at: new Date(triggerAt).toISOString(),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  },
                });
                setTriggerAt("");
              }}
            >
              Add
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Looping turns the playlist into a rotation: every item repeats one loop
 * length after its last airing, until the run is stopped. Leaving the length
 * empty makes one pass exactly as long as the playlist's own content, so it
 * restarts the moment the last item is off air.
 */
function LoopControls({
  playlist,
  bridgeUrl,
}: {
  playlist: PlaylistWithItems;
  bridgeUrl: string | undefined | null;
}) {
  const update = useUpdatePlaylist(bridgeUrl);
  const stored = playlist.loop_seconds;
  const [length, setLength] = useState(
    stored !== undefined ? formatOffset(stored) : "",
  );

  const raw = length.trim();
  const parsed = raw === "" ? null : parseOffset(raw);
  const invalid = raw !== "" && parsed === null;

  // A rejected length - shorter than the playlist's content - has to put the
  // stored value back, or the field misreports what is saved.
  const reset = () =>
    setLength(stored !== undefined ? formatOffset(stored) : "");

  const commitLength = () => {
    if (invalid) return reset();
    if (parsed === (stored ?? null)) return;
    update.mutate(
      { playlistId: playlist.id, data: { loop_seconds: parsed } },
      { onError: reset },
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-3 rounded border p-2 text-sm">
      <div className="flex items-center gap-2">
        <Repeat className="h-3 w-3 text-muted-foreground" />
        <Label htmlFor={`loop-${playlist.id}`} className="text-xs">
          Loop
        </Label>
        <Switch
          id={`loop-${playlist.id}`}
          checked={playlist.loop_enabled}
          onCheckedChange={(checked) =>
            update.mutate({
              playlistId: playlist.id,
              data: { loop_enabled: checked },
            })
          }
          aria-label="Loop playlist"
          data-testid="playlist-loop"
        />
      </div>

      {playlist.loop_enabled && (
        <div className="flex items-center gap-2">
          <Label className="text-xs">Every</Label>
          <Input
            value={length}
            onChange={(event) => setLength(event.target.value)}
            onBlur={commitLength}
            placeholder="auto"
            title="Loop length. Leave empty to restart as soon as the last item is off air."
            className={`w-24 font-mono ${invalid ? "border-destructive" : ""}`}
            data-testid="playlist-loop-length"
          />
          <span className="text-xs text-muted-foreground">
            {stored === undefined
              ? "as long as the playlist"
              : "fixed length, idle time included"}
          </span>
        </div>
      )}
    </div>
  );
}

/** A playlist item, editable in place. */
function ItemRow({
  item,
  playouts,
  bridgeUrl,
  onDelete,
}: {
  item: PlaylistItem;
  playouts: PlayoutWithAsset[];
  bridgeUrl: string | undefined | null;
  onDelete: () => void;
}) {
  const update = useUpdatePlaylistItem(bridgeUrl);
  const [offset, setOffset] = useState(formatOffset(item.offset_seconds));
  const [hold, setHold] = useState(
    item.hold_seconds !== undefined ? String(item.hold_seconds) : "",
  );

  const playout = playouts.find((p) => p.id === item.playout_id);
  // What the item will actually hold for if the override is left empty.
  const inherited = effectiveHoldSeconds(playout?.asset ?? null, null);
  const offsetSeconds = parseOffset(offset);

  // These two fields keep local state while being typed, so a rejected edit -
  // an overlap, say - has to put the stored value back. Otherwise the field
  // keeps showing what was typed and quietly misreports what is saved.
  const resetOffset = () => setOffset(formatOffset(item.offset_seconds));
  const resetHold = () =>
    setHold(item.hold_seconds !== undefined ? String(item.hold_seconds) : "");

  const commitOffset = () => {
    if (offsetSeconds === null || offsetSeconds === item.offset_seconds) {
      resetOffset();
      return;
    }
    update.mutate(
      { itemId: item.id, data: { offset_seconds: offsetSeconds } },
      { onError: resetOffset },
    );
  };

  const commitHold = () => {
    const raw = hold.trim();
    const value = raw === "" ? null : Number(raw);
    if (raw !== "" && (!Number.isFinite(value) || value! < 1)) {
      resetHold();
      return;
    }
    if (value === (item.hold_seconds ?? null)) return;
    update.mutate(
      { itemId: item.id, data: { hold_seconds: value } },
      { onError: resetHold },
    );
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded border p-2 text-sm"
      data-testid="playlist-item"
    >
      <Input
        value={offset}
        onChange={(event) => setOffset(event.target.value)}
        onBlur={commitOffset}
        className={`w-24 font-mono ${offsetSeconds === null ? "border-destructive" : ""}`}
        data-testid="item-offset"
      />

      <Select
        value={item.playout_id}
        onValueChange={(value) =>
          update.mutate({ itemId: item.id, data: { playout_id: value } })
        }
      >
        <SelectTrigger className="w-48" data-testid="item-playout">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {playouts.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-1">
        <Input
          value={hold}
          onChange={(event) => setHold(event.target.value)}
          onBlur={commitHold}
          placeholder={`${Math.round(inherited)}s`}
          title="Hold override in seconds. Leave empty to use the asset's own length."
          className="w-20"
          data-testid="item-hold"
        />
        <span className="text-xs text-muted-foreground">
          {item.hold_seconds === undefined ? "from asset" : "override"}
        </span>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <Switch
          checked={item.enabled}
          onCheckedChange={(checked) =>
            update.mutate({ itemId: item.id, data: { enabled: checked } })
          }
          aria-label="Enabled"
          data-testid="item-enabled"
        />
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

/** A scheduled start, editable in place, showing what became of it. */
function TriggerRow({
  trigger,
  bridgeUrl,
  onDelete,
}: {
  trigger: PlaylistTrigger;
  bridgeUrl: string | undefined | null;
  onDelete: () => void;
}) {
  const update = useUpdateTrigger(bridgeUrl);
  const badge = TRIGGER_BADGE[trigger.status ?? "pending"];

  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded border p-2 text-sm"
      data-testid="playlist-trigger"
    >
      <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />
      <Input
        type="datetime-local"
        defaultValue={toLocalInput(trigger.starts_at)}
        onBlur={(event) => {
          if (!event.target.value) return;
          const next = new Date(event.target.value).toISOString();
          if (next === trigger.starts_at) return;
          // Rescheduling re-arms a trigger that has already fired.
          update.mutate({
            triggerId: trigger.id,
            data: {
              starts_at: next,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          });
        }}
        className="w-56"
        data-testid="trigger-time"
      />
      <Badge variant={badge.variant} data-testid="trigger-status">
        {badge.label}
      </Badge>
      {trigger.fired_at && (
        <span className="text-xs text-muted-foreground">
          fired {formatLocal(trigger.fired_at)}
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        <Switch
          checked={trigger.enabled}
          onCheckedChange={(checked) =>
            update.mutate({
              triggerId: trigger.id,
              data: { enabled: checked },
            })
          }
          aria-label="Enabled"
          data-testid="trigger-enabled"
        />
        <Button size="sm" variant="ghost" onClick={onDelete}>
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
